import { fetchAndActivate, getValue } from 'firebase/remote-config'
import mixpanel from 'mixpanel-browser'

import { CLIENT_NAME_COOKIE, NAVIGATION_FROM_APP_CODE, ONE_CLICK_APP_SOURCE } from '@/constants/common'
import { APP_CODE_TYPE } from '@/models/Auth'

import { getCookieValue, isProductionBuild, isSessionStorageAvailable } from '../helper'
import getClientCode from '../helpers/getClientCode'
import { firebaseRemoteConfigDefaults, remoteConfig } from './firebaseConfig'

interface MixpanelProperties {
  [key: string]: any
}

interface MixpanelProps {
  track: (name: string, props?: MixpanelProperties) => void
  identify: (id: string) => void
  setUser: (props: MixpanelProperties) => void
  reset: () => void
}

let replayEnabled = false
let configValues: Record<string, any> = {}
let mixpanelInitialized = false
let eventQueue: { name: string; props?: MixpanelProperties }[] = []

const JOURNEY_EVENT_MAP: Record<string, keyof typeof configValues> = {
  UserLandedOnCommercePlatform: 'completeSelfOrderingFlow',
  UserLandedOnShopByCrop: 'completeSelfOrderingFlow',
  AddToCartClick: 'atcToCheckout',
  UserLandedonCart: 'checkout',
  ProductSearchInitiated: 'search',
}
const clientCode =
  (typeof window !== 'undefined' &&
    isSessionStorageAvailable(window) &&
    sessionStorage?.getItem(ONE_CLICK_APP_SOURCE)) ||
  getCookieValue(CLIENT_NAME_COOKIE) ||
  getClientCode(getCookieValue(NAVIGATION_FROM_APP_CODE) as APP_CODE_TYPE)

const getDefaultConfigValues = () => ({
  recordSessionsPercent: Number(
    firebaseRemoteConfigDefaults.record_sessions_percent,
  ),
  recordHeatmapData: Boolean(firebaseRemoteConfigDefaults.record_heatmap_data),
  eligibleAppSources: JSON.parse(
    firebaseRemoteConfigDefaults.eligible_app_sources,
  ),
  completeSelfOrderingFlow: Boolean(
    firebaseRemoteConfigDefaults.complete_self_ordering_flow,
  ),
  atcToCheckout: Boolean(firebaseRemoteConfigDefaults.atc_to_checkout),
  checkout: Boolean(firebaseRemoteConfigDefaults.checkout),
  search: Boolean(firebaseRemoteConfigDefaults.search),
})

export const initMixpanel = async () => {
  if (mixpanelInitialized) return
  try {
    if (remoteConfig) {
      await fetchAndActivate(remoteConfig)

      configValues = {
        recordSessionsPercent: parseInt(
          getValue(remoteConfig, 'record_sessions_percent').asString() || '0',
        ),
        recordHeatmapData: getValue(
          remoteConfig,
          'record_heatmap_data',
        ).asBoolean(),
        eligibleAppSources: JSON.parse(
          getValue(remoteConfig, 'eligible_app_sources').asString() || '[]',
        ),
        completeSelfOrderingFlow: getValue(
          remoteConfig,
          'complete_self_ordering_flow',
        ).asBoolean(),
        atcToCheckout: getValue(remoteConfig, 'atc_to_checkout').asBoolean(),
        checkout: getValue(remoteConfig, 'checkout').asBoolean(),
        search: getValue(remoteConfig, 'search').asBoolean(),
      }
    } else {
      configValues = getDefaultConfigValues()
    }

    const mixpanelToken = process.env.NEXT_PUBLIC_MIXPANEL_TOKEN
    if (!mixpanelToken) {
      console.warn('NEXT_PUBLIC_MIXPANEL_TOKEN is missing; Mixpanel is disabled.')
      return
    }

    mixpanel.init(mixpanelToken, {
      debug: false,
      track_pageview: true,
      persistence: 'localStorage',
      record_heatmap_data: configValues.recordHeatmapData,
      record_sessions_percent: configValues.recordSessionsPercent,
    } as any)

    mixpanelInitialized = true
    if (eventQueue.length > 0) {
      eventQueue.forEach(({ name, props }) => Mixpanel.track(name, props))
      eventQueue = []
    }
  } catch (error) {
    console.error('Failed to fetch Remote Config:', error)
  }
}

const Mixpanel: MixpanelProps = {
  track: (name, props) => {
    if (!mixpanelInitialized) {
      eventQueue.push({ name, props })
      return
    }
    mixpanel.track(name, props)
    if (!replayEnabled && JOURNEY_EVENT_MAP[name]) {
      const journeyKey = JOURNEY_EVENT_MAP[name]
      const isJourneyEnabled = configValues[journeyKey]
      if (isProductionBuild && isJourneyEnabled && clientCode &&
        configValues.eligibleAppSources.includes(clientCode)) {
        try {
          mixpanel.start_session_recording()
          replayEnabled = true
        } catch (err) {
          console.warn('Could not start replay:', err)
        }
      }
    }
  },
  identify: (id) => mixpanel.identify(id),
  setUser: (props) => mixpanel.people?.set(props),
  reset: () => {
    mixpanel.reset()
    replayEnabled = false
  },
}

export default Mixpanel
